import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useReceivement } from 'src/hooks/useReceivement'
import { useRouteToNextStep } from 'src/hooks/useRouteToNextStep'
import apis from '../../ReceivementApi'
import { BatchWithoutBarcode, Product } from '../../ReceivementTypes'
import * as S from './BatchesWithoutBarcode.styles'
import { sortProductsByDescription } from './BatchesWithoutBarcode.utils'
import {
  ContainerRelation,
  TableBatchBarcodeNotFound,
  TableOrdersItems,
} from './components'
import { useRelation } from './hooks/useRelation'

export default () => {
  const { receivementId } = useParams<{ receivementId: string }>()
  const { supervisorBadge } = useReceivement({ validateSupervisor: true })

  const [batchesWithoutBarcode, setBatchesWithoutBarcode] = useState<BatchWithoutBarcode[]>()
  const [ordersItems, setOrdersItems] = useState<Product[]>()

  const [selectedBatch, setSelectedBatch] = useState<BatchWithoutBarcode>()
  const [selectedProduct, setSelectedProduct] = useState<Product>()
  const [selectedNoRelation, setSelectedNoRelation] = useState(false)

  const { routeToNext } = useRouteToNextStep({
    receivementId,
    supervisorBadge,
    errorMessage: 'Falha ao finalizar a resolução de barcode!',
  })

  const resetRelation = () => {
    setSelectedBatch(undefined)
    setSelectedProduct(undefined)
    setSelectedNoRelation(false)
  }

  const verifyIfFinished = useCallback(
    (batches) => {
      if (batches.length === 0) {
        routeToNext()
        return
      }

      setBatchesWithoutBarcode(batches)
    },
    [routeToNext],
  )

  const { handleRelation } = useRelation({
    supervisorBadge,
    selectedBatch,
    selectedProduct,
    selectedNoRelation,
    batchesWithoutBarcode,
    onFinish: (batches) => {
      resetRelation()
      verifyIfFinished(batches)
    },
  })

  const handleNoRelation = () => {
    setSelectedProduct(undefined)
    setSelectedNoRelation(true)
  }

  const handleSelectedProduct = (product: Product) => {
    setSelectedNoRelation(false)
    setSelectedProduct(product)
  }

  const fetchBatchesWithoutBarcode = useCallback(async () => {
    const response = await apis.getBarcodeNotFound(supervisorBadge, receivementId)
    if (!response.isOk) {
      toast.warn(response.data.message)
      return
    }
    verifyIfFinished(response.data)
  }, [receivementId, supervisorBadge, verifyIfFinished])

  const fetchProductsFromOrderItems = useCallback(async () => {
    const response = await apis.getOrderItems(supervisorBadge, receivementId)

    if (!response.isOk) {
      toast.warn(response.data.message)
      return
    }

    const { data: orderItems } = response
    const orderItemsSorted = orderItems.sort(sortProductsByDescription)
    setOrdersItems(orderItemsSorted)
  }, [receivementId, supervisorBadge])

  useEffect(() => {
    if (!supervisorBadge) {
      return
    }
    fetchProductsFromOrderItems()
    fetchBatchesWithoutBarcode()
  }, [
    fetchBatchesWithoutBarcode,
    fetchProductsFromOrderItems,
    supervisorBadge,
  ])

  if (!batchesWithoutBarcode || !ordersItems) {
    return (
      <S.Wrapper>
        <h1>Carregando...</h1>
      </S.Wrapper>
    )
  }

  return (
    <S.Wrapper>
      <TableBatchBarcodeNotFound
        batchesNotBarcode={batchesWithoutBarcode}
        selected={selectedBatch}
        onSelected={setSelectedBatch}
      />
      <ContainerRelation
        isCheckedNoRelation={selectedNoRelation}
        onRelation={handleRelation}
        onCheckboxNoRelation={handleNoRelation}
      />
      <TableOrdersItems
        ordersItems={ordersItems}
        selected={selectedProduct}
        onSelected={handleSelectedProduct}
      />
    </S.Wrapper>
  )
}
